import styled from "styled-components";

import { QUERIES, SPACING } from "../../../../../utils/constants";
import { RateChangeDirection } from "./types";
import { BaseButton } from "../../../../../components/Button";

const BAND_TINT = "var(--color-tab-background)";

const DIRECTION_TEXT: Record<RateChangeDirection, string> = {
  up: "var(--color-error)",
  down: "var(--color-success)",
  flat: "var(--color-text-secondary)",
};

const DIRECTION_BG: Record<RateChangeDirection, string> = {
  up: "var(--color-error-background)",
  down: "var(--color-success-background)",
  flat: "color-mix(in srgb, var(--color-text-muted) 12%, transparent)",
};

const DIRECTION_BORDER: Record<RateChangeDirection, string> = {
  up: "var(--color-error-border)",
  down: "var(--color-success-border)",
  flat: "var(--color-border)",
};

const signedColor = (value: number | null): string => {
  if (value === null || value === 0) {
    return "var(--color-text-secondary)";
  }
  return value > 0 ? "var(--color-error)" : "var(--color-success)";
};

export const RateCard = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 16px;
  box-shadow: var(--shadow-small);
  overflow: hidden;
`;

export const HeaderTitle = styled.h2`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xxl);
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.1;
  color: var(--color-text-default);
  margin: 0;
`;

export const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: var(--text-sm);
  font-style: italic;
  color: var(--color-text-secondary);
`;

export const DesktopHeader = styled.div`
  display: none;

  @media ${QUERIES.laptopAndUp} {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${SPACING.lg}px;
    flex-wrap: wrap;
    padding: ${SPACING.lg}px;
    background: ${BAND_TINT};
    border-bottom: 1px solid var(--color-border);
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  min-width: 0;
`;

export const HeaderStats = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACING.xl}px;
`;

export const HeaderStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  text-align: right;
`;

export const HeaderStatLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-secondary);
`;

export const HeaderStatValue = styled.span<{ $accent?: boolean }>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  font-weight: ${({ $accent }) => ($accent ? "600" : "500")};
  letter-spacing: -0.02em;
  line-height: 1;
  white-space: nowrap;
  color: ${({ $accent }) =>
    $accent ? "var(--color-primary)" : "var(--color-text-default)"};
`;

const ROW_GRID = `
  grid-template-columns:
    minmax(110px, 1.1fr)
    minmax(90px, 2.8fr)
    minmax(150px, 1.8fr)
    minmax(110px, 0.8fr)
    36px;
`;

export const Table = styled.div`
  display: none;

  @media ${QUERIES.laptopAndUp} {
    display: block;
  }
`;

export const TableHead = styled.div`
  display: grid;
  ${ROW_GRID}
  align-items: center;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px ${SPACING.lg}px;
  background: var(--color-gray-background);
  border-bottom: 1px solid var(--color-border);
`;

export const TableHeadCell = styled.span<{ $align?: "left" | "right" }>`
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-secondary);
  text-align: ${({ $align }) => $align ?? "left"};
`;

export const TableRow = styled.div`
  display: grid;
  ${ROW_GRID}
  align-items: center;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px ${SPACING.lg}px;

  & + & {
    border-top: 1px solid var(--color-border);
  }
`;

export const Cell = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const DateValue = styled.span`
  font-size: var(--text-base);
  color: var(--color-text-default);
`;

export const RateValue = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xl);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--color-text-default);
`;

export const PaymentValue = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-lg);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--color-text-default);
`;

export const PaymentDelta = styled.span<{ $delta: number | null }>`
  font-size: var(--text-xs);
  color: ${({ $delta }) => signedColor($delta)};
`;

export const ChangeCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
`;

export const MenuCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const ChangePill = styled.span<{ $direction: RateChangeDirection }>`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-sm);
  font-weight: 600;
  white-space: nowrap;
  color: ${({ $direction }) => DIRECTION_TEXT[$direction]};
  background: ${({ $direction }) => DIRECTION_BG[$direction]};
  border: 1px solid ${({ $direction }) => DIRECTION_BORDER[$direction]};
`;

export const PillArrow = styled.span`
  font-size: var(--text-xs);
  line-height: 1;
  margin-top: ${SPACING.xs}px;
`;

export const OriginationTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-sm);
  font-weight: 500;
  white-space: nowrap;
  color: var(--color-text-secondary);
  background: color-mix(in srgb, var(--color-text-muted) 12%, transparent);
  border: 1px solid var(--color-border);
`;

export const MobileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px;
  background: ${BAND_TINT};
  border-bottom: 1px solid var(--color-border);

  @media ${QUERIES.laptopAndUp} {
    display: none;
  }
`;

export const MobileHeaderLeft = styled(BaseButton)`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  min-width: 0;
`;

export const CollapseChevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  color: var(--color-text-secondary);
  transition: transform 0.2s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? "0deg" : "-90deg")});

  svg {
    display: block;
  }
`;

export const PeriodsPill = styled.span`
  flex-shrink: 0;
  padding: ${SPACING.xs}px ${SPACING.sm}px;
  border-radius: 999px;
  font-size: var(--text-xs);
  font-weight: 500;
  white-space: nowrap;
  color: var(--color-text-secondary);
  background: var(--color-card-background);
  border: 1px solid var(--color-border);
`;

export const CardList = styled.div`
  display: flex;
  flex-direction: column;

  @media ${QUERIES.laptopAndUp} {
    display: none;
  }
`;

export const PeriodCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px;

  & + & {
    border-top: 1px solid var(--color-border);
  }
`;

export const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const CardRateWrap = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

export const CardRate = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xxl);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--color-text-default);
`;

export const CurrentBadge = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-secondary);
`;

export const CardMeta = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const CardBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const CardPayment = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${SPACING.xs}px;
  flex-wrap: wrap;
  min-width: 0;
`;

export const CardPaymentLabel = styled.span`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

export const CardPaymentValue = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--color-text-default);
`;

export const CardPaymentDelta = styled.span<{ $delta: number | null }>`
  font-size: var(--text-sm);
  color: ${({ $delta }) => signedColor($delta)};
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
  padding: ${SPACING.md}px;
  background: ${BAND_TINT};
  border-top: 1px solid var(--color-border);

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING.md}px ${SPACING.lg}px;
  }
`;

export const FooterNote = styled.span`
  font-size: var(--text-sm);
  font-style: italic;
  color: var(--color-text-secondary);
`;

export const FooterMeta = styled.span`
  flex-shrink: 0;
  font-size: var(--text-sm);
  font-style: italic;
  color: var(--color-text-muted);

  @media ${QUERIES.laptopAndUp} {
    display: none;
  }
`;
